import type { ToolMode, SubtoolName, ToolName } from "@/types/user-types";

const MODE_TO_SUBTOOLS: Record<ToolMode, readonly SubtoolName[]> = {
  sketch: ["ballpoint", "marker", "delete", "delete-clean", "marquee", "lasso"],
  edit: [
    "selection",
    "layout",
    "text",
    "shape",
    "media",
    "component",
  ],
  ds: [],
  cms: [],
};

const SUBTOOL_TO_MODE = (() => {
  const map = new Map<SubtoolName, ToolMode>();
  for (const [mode, subtools] of Object.entries(MODE_TO_SUBTOOLS)) {
    for (const subtool of subtools) {
      map.set(subtool as SubtoolName, mode as ToolMode);
    }
  }
  return map;
})();

const PRIMARY_TOOL_TO_MODE: Record<ToolName, ToolMode> = {
  draw: "sketch",
  select: "edit",
  text: "edit",
  shapes: "edit",
  pan: "sketch",
  zoom: "sketch",
};

const hasMode = (mode: ToolMode | null | undefined): mode is ToolMode =>
  typeof mode === "string" && Object.prototype.hasOwnProperty.call(MODE_TO_SUBTOOLS, mode);

export const getSubtoolsForMode = (
  mode: ToolMode | null | undefined
): readonly SubtoolName[] => {
  if (!hasMode(mode)) return [];
  return MODE_TO_SUBTOOLS[mode];
};

export const getDefaultSubtoolForMode = (
  mode: ToolMode | null | undefined
): SubtoolName | null => {
  const list = getSubtoolsForMode(mode);
  return list.length ? (list[0] as SubtoolName) : null;
};

export const ensureSubtoolForMode = (
  mode: ToolMode | null | undefined,
  desired: SubtoolName | null | undefined
): SubtoolName | null => {
  if (!hasMode(mode)) return desired ?? null;
  const subtools = getSubtoolsForMode(mode);
  if (!subtools.length) return null;
  if (desired && subtools.includes(desired)) return desired;
  return subtools[0] ?? null;
};

export const isSubtoolAllowedForMode = (
  mode: ToolMode | null | undefined,
  subtool: SubtoolName | null | undefined
): boolean => {
  if (!hasMode(mode) || !subtool) return false;
  return getSubtoolsForMode(mode).includes(subtool);
};

export const getPrimaryToolForMode = (
  mode: ToolMode | null | undefined
): ToolName => {
  if (!mode) return "select";
  switch (mode) {
    case "sketch":
      return "draw";
    case "edit":
      return "select";
    case "ds":
    case "cms":
    default:
      return "select";
  }
};

export const getModeForSubtool = (
  subtool: SubtoolName | null | undefined
): ToolMode | null => {
  if (!subtool) return null;
  return SUBTOOL_TO_MODE.get(subtool) ?? null;
};

export const getModeForPrimaryTool = (
  tool: ToolName | null | undefined
): ToolMode | null => {
  if (!tool) return null;
  return PRIMARY_TOOL_TO_MODE[tool] ?? "edit";
};
